import { Employee, TeamPerformanceData, BudgetData } from '../contexts/AppContext.tsx';
import { EmployeePerformanceData } from '../types/employeePerformance.ts';
import { defaultTaxTable } from '../data/taxTable.ts';
import { resolveTeamIncentiveRatio, isPartPosition } from './payrollCalculations.ts';

// 실시간 인센티브 계산 함수
const calculateRealTimeIncentive = (
  employee: Employee,
  teamPerformance: TeamPerformanceData,
  allEmployees: Employee[],
  budgetData?: BudgetData,
  allTeamPerformances?: Record<string, TeamPerformanceData>,
  month?: string
): number => {
  // 🔧 월급(세전) 0원 직원은 인센티브 배분에서 제외
  if (!employee.salary || employee.salary <= 0) {
    return 0;
  }

  // 인센티브 산출 매출이 0 이하면 인센티브 없음
  if (teamPerformance.incentiveCalculationSales <= 0) {
    return 0;
  }

  // 팀별 인센티브 비율 (월별 우선 → 전역 동결 폴백)
  const teamIncentiveRatio = resolveTeamIncentiveRatio(budgetData, month, employee.team);

  // 팀 전체 성과 인센티브 계산
  const teamPerformanceIncentive = teamPerformance.incentiveCalculationSales * (teamIncentiveRatio.performance / 100);

  // 실장 선공제: 실장이 존재하면 실장 몫을 먼저 차감
  const director = allEmployees.find(emp => emp.position === '실장' && emp.directorIncentiveRate);
  const directorRate = director?.directorIncentiveRate || 0;
  const directorDeduction = teamPerformanceIncentive * (directorRate / 100);
  const adjustedTeamIncentive = teamPerformanceIncentive - directorDeduction;

  // 실장의 인센티브 = 전체 팀의 선공제 합산
  if (employee.position === '실장' && directorRate > 0) {
    if (allTeamPerformances) {
      return calculateDirectorTotalIncentive(employee, allTeamPerformances, budgetData, month);
    }
    return Math.round(directorDeduction); // fallback: 단일 팀의 선공제분
  }

  // 🚫 파트 직위는 인센티브 배분 대상 아님 (팀원 분모에서도 제외되므로 받는 쪽도 제외해야 과분배 방지)
  if (employee.position === '파트') {
    return 0;
  }

  // 같은 팀 직원들 필터링
  const teamEmployees = allEmployees.filter(emp => emp.team === employee.team);

  if (employee.position === '팀장') {
    // 팀장 인센티브 = 실장 선공제 후 팀 성과 인센티브 × 팀장 비율
    return Math.round(adjustedTeamIncentive * (teamIncentiveRatio.team / 100));
  } else {
    // 팀원들 수 계산
    const teamMembers = teamEmployees.filter(emp => emp.position === '팀원' && emp.salary > 0);

    if (teamMembers.length > 0) {
      // 팀원 전체 인센티브 = 실장 선공제 후 팀 성과 인센티브 × 팀원 비율
      const teamMemberTotalIncentive = adjustedTeamIncentive * (teamIncentiveRatio.member / 100);
      // 개별 팀원 인센티브 = 팀원 전체 인센티브 ÷ 팀원 수
      return Math.round(teamMemberTotalIncentive / teamMembers.length);
    }

    return 0;
  }
};

// 소득세 계산 함수 (급여 관리와 동일한 로직)
const calculateIncomeTax = (taxableIncome: number, adjustedFamilyMembers: number): number => {
  const monthlyIncomeInThousands = Math.round(taxableIncome / 1000);
  const familyIndex = Math.min(adjustedFamilyMembers - 1, 10);
  let incomeTax = 0;

  const over10mData = defaultTaxTable.find(item => item.min === 10000);
  
  if (monthlyIncomeInThousands >= 10000) {
    if (over10mData && over10mData.taxes) {
      const baseAmount = over10mData.taxes[familyIndex] || 0;
      if (monthlyIncomeInThousands <= 14000) incomeTax = baseAmount + ((monthlyIncomeInThousands - 10000) * 0.98 * 0.35);
      else if (monthlyIncomeInThousands <= 28000) incomeTax = baseAmount + 1372000 + ((monthlyIncomeInThousands - 14000) * 0.98 * 0.38);
      else if (monthlyIncomeInThousands <= 30000) incomeTax = baseAmount + 6585600 + ((monthlyIncomeInThousands - 28000) * 0.98 * 0.40);
      else if (monthlyIncomeInThousands <= 45000) incomeTax = baseAmount + 7369600 + ((monthlyIncomeInThousands - 30000) * 0.40);
      else if (monthlyIncomeInThousands <= 87000) incomeTax = baseAmount + 13369600 + ((monthlyIncomeInThousands - 45000) * 0.42);
      else incomeTax = baseAmount + 31009600 + ((monthlyIncomeInThousands - 87000) * 0.45);
    }
  } else {
    let exactMatch = false;
    for (const bracket of defaultTaxTable) {
      if (bracket.min === 10000) continue;
      if (monthlyIncomeInThousands >= bracket.min && monthlyIncomeInThousands < bracket.max) {
        if (bracket.taxes && bracket.taxes.length > familyIndex) {
          incomeTax = bracket.taxes[familyIndex] || 0;
          exactMatch = true;
          break;
        }
      }
    }
    if (!exactMatch) {
      let closestLowerBracket: any = null;
      let closestUpperBracket: any = null;
      for (const bracket of defaultTaxTable) {
        if (bracket.min === 10000) continue;
        if (bracket.min <= monthlyIncomeInThousands) {
          if (!closestLowerBracket || bracket.min > closestLowerBracket.min) {
            closestLowerBracket = bracket;
          }
        }
        if (bracket.min > monthlyIncomeInThousands) {
          if (!closestUpperBracket || bracket.min < closestUpperBracket.min) {
            closestUpperBracket = bracket;
          }
        }
      }
      if (closestLowerBracket && closestUpperBracket && closestLowerBracket.taxes && closestUpperBracket.taxes) {
        const lowerTax = closestLowerBracket.taxes[familyIndex] || 0;
        const upperTax = closestUpperBracket.taxes[familyIndex] || 0;
        const ratio = (monthlyIncomeInThousands - closestLowerBracket.min) / (closestUpperBracket.min - closestLowerBracket.min);
        if (isFinite(ratio)) {
          incomeTax = Math.round(lowerTax + ratio * (upperTax - lowerTax));
        } else if (closestLowerBracket.min === monthlyIncomeInThousands) {
          incomeTax = lowerTax;
        } else {
          incomeTax = lowerTax; // Fallback if upper and lower are the same or ratio is not finite
        }
      } else if (closestLowerBracket && closestLowerBracket.taxes) {
        incomeTax = closestLowerBracket.taxes[familyIndex] || 0;
      } else if (closestUpperBracket && closestUpperBracket.taxes) {
        incomeTax = closestUpperBracket.taxes[familyIndex] || 0;
      }
    }
  }
  return Math.max(0, Math.round(incomeTax / 10) * 10);
};

// 세금 계산 함수 (급여 관리와 동일한 로직)
const calculateDeductions = (employee: Employee, includeAllowances: boolean = false) => {
  const { salary, familyCount = 0, nonTaxable = 0, overtime = 0, individual = 0, incentive = 0 } = employee;

  // 🔒 파트(아르바이트)는 공제 없음 — 급여관리 화면의 같은 이름 함수와 동일 규칙을 유지한다.
  //    (두 사본이 갈라지면 같은 직원의 실지급액이 화면마다 다르게 보인다)
  if (isPartPosition(employee)) {
    return {
      nationalPension: 0,
      healthInsurance: 0,
      longTermCare: 0,
      employmentInsurance: 0,
      incomeTax: 0,
      localIncomeTax: 0,
      tax: 0,
      netSalary: individual
    };
  }
  
  // 인센티브 포함 여부에 따라 과세대상소득 계산
  const baseSalary = includeAllowances ? salary + incentive + overtime + individual : salary;
  const taxableIncome = Math.max(0, baseSalary - nonTaxable);
  const adjustedFamilyMembers = familyCount + 1;

  // 과세대상소득 0원 → 모든 공제 0 (최소보험료 미적용; 급여관리 calculateDeductions와 동일 규칙)
  if (taxableIncome <= 0) {
    return {
      nationalPension: 0,
      healthInsurance: 0,
      longTermCare: 0,
      employmentInsurance: 0,
      incomeTax: 0,
      localIncomeTax: 0,
      tax: 0,
      netSalary: baseSalary
    };
  }

  const npBase = Math.min(Math.max(taxableIncome, 390000), 6170000);
  const nationalPension = Math.round(npBase * 0.045 / 10) * 10;
  
  const hiBase = Math.min(Math.max(taxableIncome, 370000), 119625000);
  const healthInsurance = Math.round(hiBase * 0.03545 / 10) * 10;
  
  const longTermCare = Math.round(healthInsurance * 0.1281 / 10) * 10;
  
  const employmentInsurance = Math.round(taxableIncome * 0.009 / 10) * 10;

  // 급여 관리와 동일한 소득세 계산 (defaultTaxTable 사용)
  const incomeTax = calculateIncomeTax(taxableIncome, adjustedFamilyMembers);
  const localIncomeTax = Math.round(incomeTax * 0.1 / 10) * 10;

  const totalTax = nationalPension + healthInsurance + longTermCare + employmentInsurance + incomeTax + localIncomeTax;
  const netSalaryVal = baseSalary - totalTax;

  return {
    nationalPension,
    healthInsurance,
    longTermCare,
    employmentInsurance,
    incomeTax,
    localIncomeTax,
    tax: totalTax,
    netSalary: netSalaryVal
  };
};

// 직원별 성과 계산 함수 (확장된 버전)
export const calculateEmployeePerformance = (
  employee: Employee,
  teamPerformance: TeamPerformanceData,
  allEmployees: Employee[] = [],
  budgetData?: BudgetData,
  useRealTimeIncentive: boolean = true,
  allTeamPerformances?: Record<string, TeamPerformanceData>,
  month?: string
): EmployeePerformanceData => {
  // 수당 계산
  const allowances = {
    meal: employee.meal || 0,
    housing: employee.housing || 0,
    overtime: employee.overtime || 0,
    individual: employee.individual || 0,
    other: employee.other || 0
  };

  const totalAllowances = Object.values(allowances).reduce((sum, val) => sum + val, 0);

  // 급여 관리와 동일한 수당 계산 (초과수당 + 개별수당)
  const payrollAllowances = (employee.overtime || 0) + (employee.individual || 0);

  // 인센티브 계산 (실시간 또는 저장된 값)
  const incentive = useRealTimeIncentive && allEmployees.length > 0
    ? calculateRealTimeIncentive(employee, teamPerformance, allEmployees, budgetData, allTeamPerformances, month)
    : (employee.incentive || 0);

  // 급여 관리와 동일한 총급여 계산 (기본급 + 인센티브 + 초과수당 + 개별수당)
  const payrollTotalSalary = employee.salary + incentive + payrollAllowances;

  // 급여 관리와 동일한 방식으로 세금 및 실지급액 계산
  const employeeWithIncentive = { ...employee, incentive };
  const deductionsWithAllowances = calculateDeductions(employeeWithIncentive, true);

  // 총 인건비 계산 (회사부담 = 급여관리 총급여 + 수당 + 퇴직금)
  const totalCost = payrollTotalSalary + totalAllowances + (employee.retirement || 0);

  return {
    employeeId: employee.id,
    employeeName: employee.name,
    teamName: employee.team,
    position: employee.position,
    baseSalary: employee.salary,
    netSalary: deductionsWithAllowances.netSalary, // 급여 관리와 동일한 방식으로 계산
    tax: deductionsWithAllowances.tax, // 급여 관리와 동일한 방식으로 계산
    incentive,
    retirement: employee.retirement || 0,
    allowances,
    totalAllowances,
    totalCost,
    payrollAllowances,
    payrollTotalSalary,
    performanceMetrics: {
      teamAchievementRate: teamPerformance.achievementRate,
      teamTargetSales: teamPerformance.targetSales,
      teamActualSales: teamPerformance.actualSales,
      teamExceededSales: teamPerformance.exceededSales,
      teamUnderperformanceSales: teamPerformance.underperformanceSales,
      teamDeductionAmount: teamPerformance.deductionAmount,
      teamIncentiveAmount: teamPerformance.incentiveAmount
    }
  };
};

// 기본 팀 성과 데이터 (팀 성과 데이터가 없을 때 사용)
export const getDefaultTeamPerformance = (teamName: string): TeamPerformanceData => ({
  teamName,
  targetSales: 0,
  actualSales: 0,
  achievementRate: 0,
  incentiveAmount: 0,
  exceededSales: 0,
  underperformanceSales: 0,
  deductionAmount: 0,
  incentiveCalculationSales: 0
});

// 실장 전체 인센티브 계산 (모든 팀의 선공제 합산)
export const calculateDirectorTotalIncentive = (
  director: Employee,
  teamPerformances: Record<string, TeamPerformanceData>,
  budgetData?: BudgetData,
  month?: string
): number => {
  if (!director.directorIncentiveRate || director.position !== '실장') return 0;

  const directorRate = director.directorIncentiveRate;
  let totalIncentive = 0;

  Object.values(teamPerformances).forEach(teamPerf => {
    if (teamPerf.incentiveCalculationSales <= 0) return;

    const teamIncentiveRatio = resolveTeamIncentiveRatio(budgetData, month, teamPerf.teamName);

    const teamPerformanceIncentive = teamPerf.incentiveCalculationSales * (teamIncentiveRatio.performance / 100);
    totalIncentive += teamPerformanceIncentive * (directorRate / 100);
  });

  return Math.round(totalIncentive);
};